// src/components/CARDetailView.jsx
import {
  Box,
  Typography,
  Divider,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { useState } from "react";
import { toast } from "react-toastify"; // Make sure to import this

export default function CARDetailView({
  card,
  onClick,
  isSelected,
  onCardAction,
  updateCardStatus, // Add this prop to handle UI refresh
}) {
  console.log("KanbanCard received props:", { onCardAction, updateCardStatus });
  //
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [statusSpinner, setStatusSpinner] = useState(false); // Add spinner state

  if (!card) return null;

  const displayCARNumber = card.carNumber || "CAR Number";
  const displayName = card.name || card.title || "Unnamed CAR";
  const cardId = card.id || "";

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleAction = (action) => async (event) => {
    event.stopPropagation();
    handleClose();

    if (!onCardAction) {
      console.error("onCardAction is not defined or not passed to KanbanCard");
      toast.error("Operation failed. Please check configuration.");
      return;
    }

    try {
      setStatusSpinner(true);
      console.log("KanbanCard: Action triggered:", action, "for card:", card);

      // Call the updateCARStatus utility directly if it's available
      const updatedCard = await onCardAction(card.id, card, action);

      if (updatedCard) {
        // Update the local UI state if provided
        if (updateCardStatus) {
          updateCardStatus(card.id, action);
          toast.success(`Successfully moved to ${action}!`);
        } else {
          // If updateCardStatus is not provided, still show success but warn about UI not refreshing
          toast.success(
            `Status changed to ${action}, but UI may need refresh!`
          );
          console.warn(
            "updateCardStatus function not provided, UI may not refresh automatically"
          );
        }
      } else {
        toast.error("Status update failed. Please try again.");
      }
    } catch (error) {
      console.error(`Error changing card status to ${action}:`, error);
      toast.error("Operation failed. Please try again.");
    } finally {
      setStatusSpinner(false);
    }
  };

  return (
    <Box
      p={3}
      bgcolor="#fafafa"
      borderRadius={2}
      boxShadow={1}
      mb={1.5}
      mt={1.5}
      onClick={onClick}
      sx={{
        cursor: "pointer",
        position: "relative",
      }}
      data-id={cardId}
      data-name={displayName}
      className="kanban-card"
    >
      {/* Add spinner overlay when status is changing */}
      {statusSpinner && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 1,
            borderRadius: 2,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {/* Three dots menu button - horizontal (straight) */}
      <IconButton
        size="small"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          padding: 0,
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={handleMenuClick}
      >
        <MoreHorizIcon
          fontSize="small"
          sx={{
            display: "block",
            margin: 0,
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleAction("Closed")}>Closed</MenuItem>
        <MenuItem onClick={handleAction("PastDue")}>Past-Due</MenuItem>
      </Menu>
      {/* Detail Fields Section */}
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography variant="body1" fontWeight="bold" color="#4338CA">
            {displayCARNumber}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            CAR Name
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {displayName}
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
}
